import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { GlobalVariables } from '../global';
import { DataService } from './data.service';
import { Howl } from 'howler';
import { forkJoin, Observable, of } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { differenceInMilliseconds, format, isToday } from 'date-fns';
import { BranchDetails, GetBranchDetails } from '../_types/api.types';
import {
  BranchSoundEffects,
  BranchSounds,
  GetBranchQueueIntegrationsOptions,
  GetBranchQueueIntegrationsRes,
  GetBookingsOptions,
  GetBookingsJson,
  PrinterStateType,
} from '../_types';
import { handleApiErrors } from 'app/common/_utils/api.util';

export const DEFAULT_SOUND_EFFECTS = {
  CALL_NEXT: GlobalVariables.CALL_NEXT_SOUND_URL,
  NEW_TOKEN: GlobalVariables.NEW_TOKEN_SOUND_URL,
  PRINTER_CALL_NEXT: GlobalVariables.CALL_NEXT_SOUND_URL,
  PRINTER_NEW_TOKEN: GlobalVariables.NEW_TOKEN_SOUND_URL,
};

const PRINTER_ERROR_POST_COOLDOWN = 1000 * 60 * 10; // 10 minutes

const DEFAULT_BRANCH_DETAILS: BranchDetails = {
  attachmentType: '',
  backgroundColor: '',
  companyName: '',
  companyId: 0,
  fontColor: '',
  fontFamily: '',
  happy: 0,
  neutral: 0,
  logo: '',
  logoOriginal: '',
  name: '',
  ticketAttachment: '',
  ticketText: '',
  id: '',
  locale: 'en-US',
  playVoiceReads: false,
  allowLanguage: false,
  featureFlags: {},
};

@Injectable()
export class BranchEndpointService {
  private basicUrl = GlobalVariables.BASE_API_URL + '/api/v1/branches';
  public sounds: Record<string, string> = {};
  public playVoiceReads = false;
  public details: BranchDetails = { ...DEFAULT_BRANCH_DETAILS };
  public soundEffects = {
    callNext: DEFAULT_SOUND_EFFECTS.CALL_NEXT,
    newToken: DEFAULT_SOUND_EFFECTS.NEW_TOKEN,
    printerCallNext: DEFAULT_SOUND_EFFECTS.CALL_NEXT,
    printerNewToken: DEFAULT_SOUND_EFFECTS.NEW_TOKEN,
  };

  constructor(
    private http: HttpClient,
    private dataService: DataService,
    private route: ActivatedRoute,
  ) {}

  postSetPrintingState(type: PrinterStateType, printerId?: string) {
    if (!printerId || !type) return;

    const storageKeyMap: Partial<Record<PrinterStateType, string>> = {
      printer_near_empty_paper: 'nearEmptyPaperPosted',
      printer_error: 'printerErrorPosted',
      printer_no_paper: 'printerErrorPosted',
    };

    const key = storageKeyMap[type];
    if (!key) return;

    const currentDate = window.localStorage.getItem(key);
    let doPost = true;

    if (currentDate) {
      if (type === 'printer_near_empty_paper') {
        doPost = !isToday(new Date(currentDate)); // already posted near empty paper today
      } else {
        doPost = differenceInMilliseconds(new Date(), new Date(currentDate)) > PRINTER_ERROR_POST_COOLDOWN; // recently posted printer error
      }
    }

    if (!doPost) return;

    window.localStorage.setItem(key, new Date().toISOString());
    if (type === 'printer_fixed') window.localStorage.removeItem('printerErrorPosted');

    const branchId = this.details?.id || this.dataService.branchData();
    const url = `${this.basicUrl}/${branchId}/printers/${printerId}/set_printing_state`;

    this.http.post(url, { type }).subscribe({
      error: (err) => {
        console.error(err);
        Sentry.captureException(err);
      },
    });
  }

  getBranchDetails(id: number | string): Observable<BranchDetails | null> {
    if (!id) return of(null);
    const url = `${this.basicUrl}/${id}/details`;

    return this.http.get<GetBranchDetails>(url).pipe(
      map((res) => {
        const { data } = res;
        this.details = {
          ...data.attributes,
          id: data.id,
        };
        this.playVoiceReads = data.attributes.allowLanguage;
        return this.details;
      }),
      catchError((err) => {
        console.error(err);
        return of(null);
      }),
    );
  }

  getBranchSound(): Observable<any> {
    const branchId = this.dataService.branchData?.();

    if (!branchId) return of(null);
    if (Object.keys(this.sounds).length > 0) return of(this.sounds);

    const tokenSoundsUrl = `${this.basicUrl}/${branchId}/sound`;
    const soundEffectsUrl = `${this.basicUrl}/${branchId}/sound_effects`;

    return forkJoin({
      tokenSounds: this.http.get<BranchSounds | 'error'>(tokenSoundsUrl).pipe(catchError((e) => of('error'))),
      soundEffects: this.http.get<BranchSoundEffects | 'error'>(soundEffectsUrl).pipe(catchError((e) => of('error'))),
    }).pipe(
      map(({ tokenSounds, soundEffects }) => {
        if (tokenSounds !== 'error') {
          const { data } = tokenSounds as BranchSounds;
          const { audioAttachments } = data.attributes;

          this.sounds = {};
          audioAttachments.forEach((it) => (this.sounds[it.tokenNumber] = it.attachment));
        }

        if (soundEffects !== 'error') {
          const {
            next_pending_token_sound,
            new_token_created_sound,
            printer_next_pending_token_sound,
            printer_next_token_created_sound: printer_new_token_created_sound,
          } = soundEffects as BranchSoundEffects;

          this.soundEffects = {
            callNext: next_pending_token_sound || DEFAULT_SOUND_EFFECTS.CALL_NEXT,
            newToken: new_token_created_sound || DEFAULT_SOUND_EFFECTS.NEW_TOKEN,
            printerCallNext: printer_next_pending_token_sound || DEFAULT_SOUND_EFFECTS.CALL_NEXT,
            printerNewToken: printer_new_token_created_sound || DEFAULT_SOUND_EFFECTS.NEW_TOKEN,
          };
        }

        return this.soundEffects;
      }),
    );
  }

  load(): Observable<BranchDetails | null> {
    if (this.details?.id) return of(this.details);

    const branchId = this.dataService.branchData();
    if (!branchId) return of(null);

    return forkJoin({
      details: this.getBranchDetails(branchId),
      sounds: this.getBranchSound(),
    }).pipe(map(({ details }) => details));
  }

  getBranchQueueIntegrations(options: GetBranchQueueIntegrationsOptions): Observable<GetBranchQueueIntegrationsRes> {
    const { branchId = this.dataService.branchData?.() } = options || {};

    const url = `${this.basicUrl}/${branchId}/branch_queue_integrations`;
    const params: any = { skip_pagination: true };

    return this.http
      .get<GetBranchQueueIntegrationsRes>(url, { params })
      .pipe(catchError((err) => handleApiErrors(err)));
  }

  getBookings(options: GetBookingsOptions) {
    const {
      branchId = this.dataService.branchData?.(),
      status = '',
      dateRange = format(new Date(), 'dd/MM/yyyy - dd/MM/yyyy'),
    } = options || {};

    const path = `${this.basicUrl}/${branchId}/bookings`;

    let params: any = {
      date_range: dateRange,
      skip_pagination: true,
      status,
    };

    return this.http.get<GetBookingsJson>(path, { params }).pipe(catchError((err) => handleApiErrors(err)));
  }

  playTokenEventSound(soundFile?: string, tokenNumber?: string | number) {
    let tokenReadSound = '';
    if (tokenNumber) tokenReadSound = this.sounds[tokenNumber];

    let soundToPlay = this.soundEffects.callNext;

    if (soundFile) soundToPlay = soundFile;
    else if (this.playVoiceReads && tokenReadSound) soundToPlay = tokenReadSound;

    let sound: Howl;

    console.log(`playing sound for ${tokenNumber}`);
    sound = new Howl({ src: [soundToPlay], autoplay: true });
    sound.play();
  }

  logToServer(logs: { [key: string]: string }) {
    const url = `${GlobalVariables.BASE_API_URL}/api/v1/log_event`;
    return this.http.post(url, logs).subscribe();
  }

  updateBranchQueueForcedDisabled(branchQueueId: string, forcedDisabled: boolean): Observable<any> {
    const branchId = this.details?.id || this.dataService.branchData();
    const url = `${this.basicUrl}/${branchId}/branch_queues/${branchQueueId}`;

    return this.http
      .put(url, { branch_queue: { forced_disabled: forcedDisabled } })
      .pipe(catchError((err) => handleApiErrors(err)));
  }
}
